import { ChartSvg, ChartTitle } from "@/components/tool-kit/charts/chart-svg";
import { px } from "@/components/tool-kit/charts/marks";
import {
  CHART_FONT_SIZE,
  CHART_PADDING,
  layoutTitle,
} from "@/lib/tool-kit/charts/layout";
import { niceStep } from "@/lib/tool-kit/charts/scale";
import { estimateTextWidth, truncateText } from "@/lib/tool-kit/charts/text";
import type {
  LaidOutNode,
  TreeLayout,
} from "@/lib/tool-kit/ecology/phylo-layout";
import { CHART_INK } from "@/lib/tool-kit/palette";

/*
 * A rectangular tree from `layoutTree`. Every x is a pixel position computed from the
 * layout's units, rounded with `px` so the server and the browser print the same path.
 */

const ROW = 20;
const TIP_FONT = CHART_FONT_SIZE.tick;
const SMALL_FONT = 10;
const STEM = 12;
const MAX_LABEL = 220;
const LABEL_GAP = 8;
const MIN_PLOT = 280;

export interface TreeOptions {
  showLengths: boolean;
  showLabels: boolean;
  alignTips: boolean;
  italic: boolean;
  /** Hex colour of the branches. */
  color: string;
}

type Format = (value: number, options?: Intl.NumberFormatOptions) => string;

function nodeLabel(node: LaidOutNode): string {
  return [node.name, node.support].filter(Boolean).join(" ");
}

export function TreeDiagram({
  id,
  layout,
  options,
  title,
  fmt,
  label,
  description,
}: {
  id: string;
  layout: TreeLayout;
  options: TreeOptions;
  title: string;
  fmt: Format;
  label: string;
  description: string;
}) {
  const { nodes, tips } = layout;
  const phylogram = layout.style === "phylogram";
  const tipText = (node: LaidOutNode) =>
    truncateText(node.name ?? "", MAX_LABEL, { fontSize: TIP_FONT });
  const labelWidth = Math.max(
    0,
    ...tips.map((tip) =>
      estimateTextWidth(tipText(nodes[tip]), { fontSize: TIP_FONT })
    )
  );
  const width = Math.max(
    640,
    Math.ceil(
      CHART_PADDING + STEM + MIN_PLOT + LABEL_GAP + labelWidth + CHART_PADDING
    )
  );
  const heading = layoutTitle(title, width);
  const top = CHART_PADDING + (heading?.height ?? 0) + 6;
  const plotLeft = CHART_PADDING + STEM;
  const plotRight = width - CHART_PADDING - LABEL_GAP - labelWidth;
  const scale = (plotRight - plotLeft) / (layout.maxX || 1);
  const barLength = phylogram ? niceStep(layout.maxX, 5) : 0;
  const bottom = phylogram ? 46 : 14;
  const height = Math.ceil(top + tips.length * ROW + bottom);
  const x = (node: LaidOutNode) => plotLeft + node.x * scale;
  const y = (node: LaidOutNode) => top + ROW / 2 + node.y * ROW;
  const stroke = options.color;
  const root = nodes[0];
  const aligned = !phylogram || options.alignTips;
  const labelX = (node: LaidOutNode) =>
    aligned ? plotRight + LABEL_GAP : x(node) + LABEL_GAP;
  const lines: { key: string; d: string }[] = [
    { key: "stem", d: `M${px(plotLeft - STEM)} ${px(y(root))}H${px(x(root))}` },
  ];
  for (const node of nodes) {
    if (node.parent !== null) {
      lines.push({
        key: `h${node.id}`,
        d: `M${px(x(nodes[node.parent]))} ${px(y(node))}H${px(x(node))}`,
      });
    }
    if (node.children.length > 0) {
      const first = nodes[node.children[0]];
      const last = nodes[node.children.at(-1) ?? 0];
      lines.push({
        key: `v${node.id}`,
        d: `M${px(x(node))} ${px(y(first))}V${px(y(last))}`,
      });
    }
  }
  return (
    <ChartSvg
      description={description}
      height={height}
      id={id}
      label={label}
      width={width}
    >
      <ChartTitle title={heading} />
      {phylogram && options.alignTips
        ? tips.map((tip) => {
            const node = nodes[tip];
            return x(node) + 4 < plotRight ? (
              <line
                key={`lead${tip}`}
                stroke={CHART_INK.grid}
                strokeDasharray="2 3"
                strokeWidth={1}
                x1={px(x(node) + 3)}
                x2={px(plotRight + 3)}
                y1={px(y(node))}
                y2={px(y(node))}
              />
            ) : null;
          })
        : null}
      <g fill="none" stroke={stroke} strokeLinecap="square" strokeWidth={1.6}>
        {lines.map((line) => (
          <path d={line.d} key={line.key} />
        ))}
      </g>
      {tips.map((tip) => {
        const node = nodes[tip];
        return (
          <text
            fill={CHART_INK.text}
            fontSize={TIP_FONT}
            fontStyle={options.italic ? "italic" : undefined}
            key={`tip${tip}`}
            x={px(labelX(node))}
            y={px(y(node) + TIP_FONT * 0.35)}
          >
            {tipText(node)}
          </text>
        );
      })}
      {nodes.map((node) => {
        const text = nodeLabel(node);
        return options.showLabels && !node.isLeaf && text ? (
          <text
            fill={CHART_INK.muted}
            fontSize={SMALL_FONT}
            key={`label${node.id}`}
            textAnchor="end"
            x={px(x(node) - 4)}
            y={px(y(node) - 4)}
          >
            {text}
          </text>
        ) : null;
      })}
      {phylogram && options.showLengths
        ? nodes.map((node) => {
            if (node.parent === null || node.length === null) {
              return null;
            }
            const text = fmt(node.length, { maximumSignificantDigits: 3 });
            const from = x(nodes[node.parent]);
            const room = x(node) - from;
            if (room < estimateTextWidth(text, { fontSize: SMALL_FONT }) + 6) {
              return null;
            }
            return (
              <text
                fill={CHART_INK.muted}
                fontSize={SMALL_FONT}
                key={`len${node.id}`}
                textAnchor="middle"
                x={px(from + room / 2)}
                y={px(y(node) + 13)}
              >
                {text}
              </text>
            );
          })
        : null}
      {phylogram ? (
        <g>
          <path
            d={`M${px(plotLeft)} ${px(height - 30)}H${px(plotLeft + barLength * scale)}M${px(plotLeft)} ${px(height - 34)}V${px(height - 26)}M${px(plotLeft + barLength * scale)} ${px(height - 34)}V${px(height - 26)}`}
            fill="none"
            stroke={CHART_INK.text}
            strokeWidth={1.4}
          />
          <text
            fill={CHART_INK.text}
            fontSize={SMALL_FONT + 1}
            textAnchor="middle"
            x={px(plotLeft + (barLength * scale) / 2)}
            y={px(height - 12)}
          >
            {fmt(barLength, { maximumSignificantDigits: 3 })}
          </text>
        </g>
      ) : null}
    </ChartSvg>
  );
}
